/* Recent analyses and comparisons, kept in this browser only. Storage may be
   unavailable (private mode, blocked site data), so every access is guarded
   and the app works the same without it. */

import type { Analysis, Comparison } from "./types";

export interface AnalysisEntry {
  kind: "analyse";
  id: string;
  at: number;
  name: string;
  data: Analysis;
}

export interface CompareEntry {
  kind: "compare";
  id: string;
  at: number;
  names: [string, string];
  data: Comparison;
}

export type HistoryEntry = AnalysisEntry | CompareEntry;

const KEY = "spicescore.history.v1";
const LIMIT = 12;

export function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function save(list: HistoryEntry[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, LIMIT)));
  } catch {
    // Quota or blocked storage: history is a convenience, so drop it quietly.
  }
}

export function newId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export function addHistory(entry: HistoryEntry): void {
  save([entry, ...loadHistory().filter((e) => e.id !== entry.id)]);
}

export function findHistory(id: string): HistoryEntry | undefined {
  return loadHistory().find((e) => e.id === id);
}

export function clearHistory(): void {
  save([]);
}

export function timeAgo(at: number): string {
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
